import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { exportLeadsCsv, fetchAllLeads, fetchTelecallers } from '../api';
import { hasManualLeadAccess } from '../config';
import type { Lead, Telecaller } from '../types';
import { STATUSES, STATUS_LABEL } from '../types';
import LeadCard from './LeadCard';
import ManualLeadModal from './ManualLeadModal';
import {
  QUALIFIED_REASON_CATEGORIES,
  QUALIFIED_REASON_OPTIONS_BY_CATEGORY,
  type QualifiedReasonCategory,
} from '../constants/leadReasons';

type DatePreset = '' | 'today' | 'yesterday' | 'last_7' | 'last_30' | 'range';
type FilterSection =
  | 'lead_details'
  | 'campaign_source'
  | 'followup'
  | 'lead_status'
  | 'lead_state'
  | 'reason'
  | 'creation_date'
  | 'assignment_date'
  | 'disposition_date';

const PAGE_SIZE = 15;
const STALE_TIME_MS = 60 * 1000;

type LeadsCacheEntry = {
  items: Lead[];
  page: number;
  hasMore: boolean;
  lastFetchedAt: number;
  isLoading: boolean;
  error: string | null;
};

const leadsCache = new Map<string, LeadsCacheEntry>();

function createLeadsCacheEntry(): LeadsCacheEntry {
  return {
    items: [],
    page: 0,
    hasMore: true,
    lastFetchedAt: 0,
    isLoading: false,
    error: null,
  };
}

function mergeLeadsById(existing: Lead[], incoming: Lead[]) {
  const seen = new Set<string>();
  return [...existing, ...incoming]
    .filter((lead) => {
      if (!lead.id || seen.has(lead.id)) return false;
      seen.add(lead.id);
      return true;
    })
    .sort((a, b) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime());
}

const FILTER_SECTIONS: { id: FilterSection; label: string }[] = [
  { id: 'lead_details', label: 'Lead Details' },
  { id: 'campaign_source', label: 'Campaign / Source' },
  { id: 'followup', label: 'Follow-up' },
  { id: 'lead_status', label: 'Lead Status' },
  { id: 'lead_state', label: 'Lead State' },
  { id: 'reason', label: 'Reason' },
  { id: 'creation_date', label: 'Creation Date' },
  { id: 'assignment_date', label: 'Assignment Date' },
  { id: 'disposition_date', label: 'Disposition Date' },
];

const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: '', label: 'Any date' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7', label: 'Last 7 days' },
  { value: 'last_30', label: 'Last 30 days' },
  { value: 'range', label: 'Date range' },
];

const CAMPAIGN_OPTIONS = [
  { value: 'missed_calls', label: 'Missed Calls' },
  { value: 'direct_calls', label: 'Direct Calls' },
  { value: 'whatsapp_chatbot', label: 'WhatsApp / Chatbot' },
  { value: 'facebook_ads_campaign', label: 'Facebook Ads Campaign' },
  { value: 'manual', label: 'Manual' },
  { value: 'website_form', label: 'Website Form' },
  { value: 'neodove_third_party', label: 'NeoDove / Third-party' },
  { value: 'feedback', label: 'Feedback' },
];

const FOLLOWUP_OPTIONS = [
  { value: '', label: 'Any Follow-up' },
  { value: 'no_followup', label: 'No Follow-Up' },
  { value: 'missed', label: 'Missed' },
  { value: 'today', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'next_7_days', label: 'Next 7 Days' },
  { value: 'next_30_days', label: 'Next 30 Days' },
  { value: 'upcoming_followups', label: 'Upcoming Follow-ups' },
  { value: 'any_followup', label: 'Any Follow-Up' },
  { value: 'date_range', label: 'Date Range' },
];

const LEAD_STATUS_OPTIONS = [
  { value: 'uncontacted', label: 'Uncontacted' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'in_progress', label: 'In-Progress' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'not_connected', label: 'Not Connected' },
];

function startOfLocalDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseLocalDateString(dateStr: string) {
  if (!dateStr) return null;
  return new Date(`${dateStr}T00:00:00`);
}

function presetRange(preset: DatePreset, from: string, to: string) {
  const today = new Date();
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  if (preset === 'today') {
    return { from: startOfLocalDay(today).toISOString(), to: tomorrow.toISOString() };
  }
  if (preset === 'yesterday') {
    const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    const todayStart = startOfLocalDay(today);
    return { from: yesterday.toISOString(), to: todayStart.toISOString() };
  }
  if (preset === 'last_7') {
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6);
    return { from: start.toISOString(), to: tomorrow.toISOString() };
  }
  if (preset === 'last_30') {
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29);
    return { from: start.toISOString(), to: tomorrow.toISOString() };
  }
  if (preset === 'range') {
    const rangeFrom = parseLocalDateString(from);
    const rangeTo = parseLocalDateString(to);
    const rangeFromIso = rangeFrom
      ? new Date(rangeFrom.getFullYear(), rangeFrom.getMonth(), rangeFrom.getDate()).toISOString()
      : from;
    const rangeToEnd = rangeTo
      ? new Date(rangeTo.getFullYear(), rangeTo.getMonth(), rangeTo.getDate() + 1).toISOString()
      : to;
    return { from: rangeFromIso, to: rangeToEnd };
  }
  return { from: '', to: '' };
}

function chipClass(active: boolean) {
  return active
    ? 'border-wechat-accent bg-wechat-accent text-white'
    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50';
}

function fmtLabel(text: string) {
  return text.replace(/_/g, ' ');
}

export default function AllLeadsTab({
  active,
  onSelect,
  reloadKey,
  me,
  selectedId,
  role,
}: {
  active: boolean;
  onSelect: (id: string) => void;
  reloadKey: number;
  me: string;
  selectedId?: string | null;
  role: string;
}) {
  const isPrivileged = role === 'admin' || role === 'manager' || role === 'super_admin';
  const canSeeAllLeads = isPrivileged;
  const canCreateManualLead = isPrivileged || hasManualLeadAccess(role, me);

  const [searchText, setSearchText] = useState('');
  const [q, setQ] = useState('');
  const [detailName, setDetailName] = useState('');
  const [detailPhone, setDetailPhone] = useState('');
  const [detailEmail, setDetailEmail] = useState('');
  const [detailLeadId, setDetailLeadId] = useState('');
  const [campaignFilters, setCampaignFilters] = useState<string[]>([]);
  const [followup, setFollowup] = useState('');
  const [followupFrom, setFollowupFrom] = useState('');
  const [followupTo, setFollowupTo] = useState('');
  const [leadStatusFilters, setLeadStatusFilters] = useState<string[]>([]);
  const [leadStateFilters, setLeadStateFilters] = useState<string[]>([]);
  const [reasonCategoryFilter, setReasonCategoryFilter] = useState<QualifiedReasonCategory | ''>('');
  const [reasonCodeFilter, setReasonCodeFilter] = useState('');
  const [createdPreset, setCreatedPreset] = useState<DatePreset>('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [assignmentPreset, setAssignmentPreset] = useState<DatePreset>('');
  const [assignmentFrom, setAssignmentFrom] = useState('');
  const [assignmentTo, setAssignmentTo] = useState('');
  const [dispositionPreset, setDispositionPreset] = useState<DatePreset>('');
  const [dispositionFrom, setDispositionFrom] = useState('');
  const [dispositionTo, setDispositionTo] = useState('');
  const [includeClosed, setIncludeClosed] = useState(false);
  const [assignedAgent, setAssignedAgent] = useState('');
  const [telecallers, setTelecallers] = useState<Telecaller[]>([]);
  const [showManualLeadModal, setShowManualLeadModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilterSection, setActiveFilterSection] = useState<FilterSection>('lead_details');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const telecallersLoadedRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const reloadKeyRef = useRef(reloadKey);

  useEffect(() => {
    const timer = window.setTimeout(() => setQ(searchText.trim()), 700);
    return () => window.clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (!active || !isPrivileged || telecallersLoadedRef.current) return;
    telecallersLoadedRef.current = true;
    fetchTelecallers()
      .then((d) => setTelecallers(d.telecallers || []))
      .catch(() => setTelecallers([]));
  }, [active, isPrivileged]);

  const qsBase = useMemo(() => {
    const created = presetRange(createdPreset, createdFrom, createdTo);
    const assignment = presetRange(assignmentPreset, assignmentFrom, assignmentTo);
    const disposition = presetRange(dispositionPreset, dispositionFrom, dispositionTo);

    return {
      q,
      name: detailName.trim(),
      phone: detailPhone.trim(),
      email: detailEmail.trim(),
      leadId: detailLeadId.trim(),
      campaigns: campaignFilters,
      followup,
      followupFrom: followup === 'date_range' ? followupFrom : '',
      followupTo: followup === 'date_range' ? followupTo : '',
      leadStatuses: leadStatusFilters,
      statuses: leadStateFilters,
      createdFrom: created.from,
      createdTo: created.to,
      assignmentFrom: assignment.from,
      assignmentTo: assignment.to,
      dispositionFrom: disposition.from,
      dispositionTo: disposition.to,
      reasonGroup: reasonCategoryFilter ? 'qualified_issue' : '',
      reasonCategory: reasonCategoryFilter,
      reasonCode: reasonCodeFilter,
      includeClosed,
      assigned: isPrivileged ? assignedAgent : canSeeAllLeads ? '' : 'me',
    };
  }, [
    q,
    detailName,
    detailPhone,
    detailEmail,
    detailLeadId,
    campaignFilters,
    followup,
    followupFrom,
    followupTo,
    leadStatusFilters,
    leadStateFilters,
    createdPreset,
    createdFrom,
    createdTo,
    assignmentPreset,
    assignmentFrom,
    assignmentTo,
    dispositionPreset,
    dispositionFrom,
    dispositionTo,
    reasonCategoryFilter,
    reasonCodeFilter,
    includeClosed,
    assignedAgent,
    isPrivileged,
    canSeeAllLeads,
  ]);

  const queryKey = useMemo(
    () => `leads:${canSeeAllLeads ? 'all' : 'me'}:${JSON.stringify(qsBase)}`,
    [canSeeAllLeads, qsBase]
  );

  const syncFromCache = useCallback((key: string) => {
    const cached = leadsCache.get(key);
    if (!cached) return false;
    setLeads(cached.items);
    setHasMore(cached.hasMore);
    setErr(cached.error);
    setLoading(false);
    setLoadingMore(cached.isLoading);
    return true;
  }, []);

  const loadPage = useCallback(async (key: string, nextPage: number, replace = false) => {
    const existing = leadsCache.get(key) ?? createLeadsCacheEntry();
    if (existing.isLoading) return;

    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    leadsCache.set(key, { ...existing, isLoading: true, error: null });
    setErr(null);
    if (replace || existing.items.length === 0) {
      setLoading(true);
      setRefreshing(false);
    } else {
      setLoadingMore(true);
    }

    try {
      const result = await fetchAllLeads({
        ...qsBase,
        limit: PAGE_SIZE,
        offset: (nextPage - 1) * PAGE_SIZE,
      }, controller.signal);
      const items = mergeLeadsById(replace ? [] : existing.items, result.leads || []);
      const updated: LeadsCacheEntry = {
        items,
        page: nextPage,
        hasMore: Boolean(result.has_more),
        lastFetchedAt: Date.now(),
        isLoading: false,
        error: null,
      };
      leadsCache.set(key, updated);
      setLeads(updated.items);
      setHasMore(updated.hasMore);
    } catch (e) {
      if (controller.signal.aborted) return;
      const message = e instanceof Error ? e.message : 'load failed';
      leadsCache.set(key, { ...existing, isLoading: false, error: message });
      setErr(message);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
        setRefreshing(false);
        setLoadingMore(false);
      }
    }
  }, [qsBase]);

  useEffect(() => {
    if (!active) return;
    const forceRefresh = reloadKeyRef.current !== reloadKey;
    if (forceRefresh) {
      leadsCache.delete(queryKey);
      reloadKeyRef.current = reloadKey;
    }
    const cached = leadsCache.get(queryKey);
    const fresh = cached && Date.now() - cached.lastFetchedAt < STALE_TIME_MS;
    if (cached) syncFromCache(queryKey);
    if (forceRefresh || !fresh) {
      const timer = window.setTimeout(() => {
        void loadPage(queryKey, 1, true);
      }, q ? 250 : 0);
      return () => {
        window.clearTimeout(timer);
        abortRef.current?.abort();
      };
    }
    return () => abortRef.current?.abort();
  }, [active, loadPage, q, queryKey, reloadKey, syncFromCache]);

  useEffect(() => {
    if (!active) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const cached = leadsCache.get(queryKey);
        if (!entries[0]?.isIntersecting || !cached?.hasMore || cached.isLoading) return;
        void loadPage(queryKey, cached.page + 1);
      },
      { rootMargin: '260px 0px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [active, loadPage, queryKey]);

  const activeChips = useMemo(() => {
    const chips: { key: string; label: string; onClear: () => void }[] = [];

    if (q) chips.push({ key: 'q', label: `Quick: ${q}`, onClear: () => { setSearchText(''); setQ(''); } });
    if (detailName) chips.push({ key: 'name', label: `Name: ${detailName}`, onClear: () => setDetailName('') });
    if (detailPhone) chips.push({ key: 'phone', label: `Phone: ${detailPhone}`, onClear: () => setDetailPhone('') });
    if (detailEmail) chips.push({ key: 'email', label: `Email: ${detailEmail}`, onClear: () => setDetailEmail('') });
    if (detailLeadId) chips.push({ key: 'leadId', label: `Lead ID: ${detailLeadId}`, onClear: () => setDetailLeadId('') });
    campaignFilters.forEach((c) =>
      chips.push({
        key: `campaign:${c}`,
        label: fmtLabel(CAMPAIGN_OPTIONS.find((item) => item.value === c)?.label ?? c),
        onClear: () => setCampaignFilters((current) => current.filter((item) => item !== c)),
      })
    );
    if (followup) {
      chips.push({
        key: `followup:${followup}`,
        label: `Follow-up: ${fmtLabel(FOLLOWUP_OPTIONS.find((item) => item.value === followup)?.label ?? followup)}`,
        onClear: () => setFollowup(''),
      });
    }
    leadStatusFilters.forEach((status) =>
      chips.push({
        key: `status:${status}`,
        label: `Status: ${LEAD_STATUS_OPTIONS.find((item) => item.value === status)?.label ?? status}`,
        onClear: () => setLeadStatusFilters((current) => current.filter((item) => item !== status)),
      })
    );
    leadStateFilters.forEach((status) =>
      chips.push({
        key: `state:${status}`,
        label: `State: ${STATUS_LABEL[status as keyof typeof STATUS_LABEL] ?? status}`,
        onClear: () => setLeadStateFilters((current) => current.filter((item) => item !== status)),
      })
    );
    if (reasonCategoryFilter) {
      const categoryLabel =
        QUALIFIED_REASON_CATEGORIES.find((item) => item.code === reasonCategoryFilter)?.label ??
        reasonCategoryFilter;
      const reasonLabel =
        reasonCategoryFilter === 'infertility_issue' && reasonCodeFilter
          ? QUALIFIED_REASON_OPTIONS_BY_CATEGORY.infertility_issue.find(
              (item) => item.code === reasonCodeFilter
            )?.label ?? reasonCodeFilter
          : '';
      chips.push({
        key: `reason:${reasonCategoryFilter}:${reasonCodeFilter}`,
        label: reasonLabel ? `Reason: ${categoryLabel} / ${reasonLabel}` : `Reason: ${categoryLabel}`,
        onClear: () => {
          setReasonCategoryFilter('');
          setReasonCodeFilter('');
        },
      });
    }
    if (createdPreset || createdFrom || createdTo) {
      chips.push({ key: 'created', label: 'Creation date', onClear: () => { setCreatedPreset(''); setCreatedFrom(''); setCreatedTo(''); } });
    }
    if (assignmentPreset || assignmentFrom || assignmentTo) {
      chips.push({ key: 'assignment', label: 'Assignment date', onClear: () => { setAssignmentPreset(''); setAssignmentFrom(''); setAssignmentTo(''); } });
    }
    if (dispositionPreset || dispositionFrom || dispositionTo) {
      chips.push({ key: 'disposition', label: 'Disposition date', onClear: () => { setDispositionPreset(''); setDispositionFrom(''); setDispositionTo(''); } });
    }
    if (includeClosed) chips.push({ key: 'closed', label: 'Include closed', onClear: () => setIncludeClosed(false) });
    if (isPrivileged && assignedAgent) {
      const agentLabel = telecallers.find((t) => t.email === assignedAgent)?.name || assignedAgent;
      chips.push({ key: 'agent', label: `Agent: ${agentLabel}`, onClear: () => setAssignedAgent('') });
    }

    return chips;
  }, [
    q,
    detailName,
    detailPhone,
    detailEmail,
    detailLeadId,
    campaignFilters,
    followup,
    leadStatusFilters,
    leadStateFilters,
    createdPreset,
    createdFrom,
    createdTo,
    assignmentPreset,
    assignmentFrom,
    assignmentTo,
    dispositionPreset,
    dispositionFrom,
    dispositionTo,
    reasonCategoryFilter,
    reasonCodeFilter,
    includeClosed,
    isPrivileged,
    assignedAgent,
    telecallers,
  ]);

  const activeFilterCount = activeChips.length;

  function clearFilters() {
    setSearchText('');
    setQ('');
    setDetailName('');
    setDetailPhone('');
    setDetailEmail('');
    setDetailLeadId('');
    setCampaignFilters([]);
    setFollowup('');
    setFollowupFrom('');
    setFollowupTo('');
    setLeadStatusFilters([]);
    setLeadStateFilters([]);
    setReasonCategoryFilter('');
    setReasonCodeFilter('');
    setCreatedPreset('');
    setCreatedFrom('');
    setCreatedTo('');
    setAssignmentPreset('');
    setAssignmentFrom('');
    setAssignmentTo('');
    setDispositionPreset('');
    setDispositionFrom('');
    setDispositionTo('');
    setIncludeClosed(false);
    setAssignedAgent('');
  }

  function toggleStringValue(current: string[], value: string) {
    return current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];
  }

  function renderDateChoices(
    value: DatePreset,
    onChange: (value: DatePreset) => void,
    from: string,
    to: string,
    setFrom: (value: string) => void,
    setTo: (value: string) => void
  ) {
    return (
      <div className="space-y-3">
        {DATE_PRESETS.filter((p) => p.value).map((preset) => (
          <label key={preset.value} className="flex items-center gap-3 text-sm font-medium text-slate-800">
            <input
              type="radio"
              checked={value === preset.value}
              onChange={() => onChange(preset.value)}
              className="h-4 w-4 accent-purple-700"
            />
            {preset.label}
          </label>
        ))}
        {value === 'range' && (
          <div className="grid grid-cols-2 gap-2">
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="min-w-0 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs"
            />
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="min-w-0 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs"
            />
          </div>
        )}
      </div>
    );
  }

  function renderRightPanel() {
    if (activeFilterSection === 'lead_details') {
      return (
        <div className="space-y-4">
          <div>
            <div className="text-sm font-semibold text-slate-700">Search fields</div>
            <div className="mt-1 text-xs text-slate-500">Search is always server-side and scoped by role.</div>
          </div>

          <input
            value={detailName}
            onChange={(e) => setDetailName(e.target.value)}
            placeholder="Search by name"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
          />
          <input
            value={detailPhone}
            onChange={(e) => setDetailPhone(e.target.value)}
            placeholder="Search by phone"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
          />
          <input
            value={detailEmail}
            onChange={(e) => setDetailEmail(e.target.value)}
            placeholder="Search by email"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
          />
          <input
            value={detailLeadId}
            onChange={(e) => setDetailLeadId(e.target.value)}
            placeholder="Search by lead ID"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
          />

          {isPrivileged && (
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Filter by agent</div>
              <select
                value={assignedAgent}
                onChange={(e) => setAssignedAgent(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">All agents</option>
                {telecallers.map((t) => (
                  <option key={t.email} value={t.email}>
                    {t.name ? `${t.name} (${t.email})` : t.email}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      );
    }

    if (activeFilterSection === 'campaign_source') {
      return (
        <div className="space-y-3">
          {CAMPAIGN_OPTIONS.map((option) => {
            const checked = campaignFilters.includes(option.value);
            return (
              <label
                key={option.value}
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => setCampaignFilters((current) => toggleStringValue(current, option.value))}
                  className="h-4 w-4 accent-purple-700"
                />
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>
      );
    }

    if (activeFilterSection === 'followup') {
      return (
        <div className="space-y-3">
          {FOLLOWUP_OPTIONS.map((option) => (
            <label key={option.value} className="flex items-center gap-3 text-sm font-medium text-slate-800">
              <input
                type="radio"
                checked={followup === option.value}
                onChange={() => setFollowup(option.value)}
                className="h-4 w-4 accent-purple-700"
              />
              {option.label}
            </label>
          ))}
          {followup === 'date_range' && (
            <div className="grid grid-cols-2 gap-2 pt-1">
              <input
                type="date"
                value={followupFrom}
                onChange={(e) => setFollowupFrom(e.target.value)}
                className="min-w-0 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs"
              />
              <input
                type="date"
                value={followupTo}
                onChange={(e) => setFollowupTo(e.target.value)}
                className="min-w-0 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs"
              />
            </div>
          )}
        </div>
      );
    }

    if (activeFilterSection === 'lead_status') {
      return (
        <div className="space-y-3">
          {LEAD_STATUS_OPTIONS.map((option) => {
            const checked = leadStatusFilters.includes(option.value);
            return (
              <label
                key={option.value}
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => setLeadStatusFilters((current) => toggleStringValue(current, option.value))}
                  className="h-4 w-4 accent-purple-700"
                />
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>
      );
    }

    if (activeFilterSection === 'lead_state') {
      return (
        <div className="space-y-3">
          {STATUSES.map((status) => {
            const checked = leadStateFilters.includes(status);
            return (
              <label
                key={status}
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => setLeadStateFilters((current) => toggleStringValue(current, status))}
                  className="h-4 w-4 accent-purple-700"
                />
                <span>{STATUS_LABEL[status]}</span>
              </label>
            );
          })}
        </div>
      );
    }

    if (activeFilterSection === 'reason') {
      const reasonOptions =
        reasonCategoryFilter === 'infertility_issue'
          ? QUALIFIED_REASON_OPTIONS_BY_CATEGORY.infertility_issue
          : [];

      return (
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Reason group
            </label>
            <select
              value={reasonCategoryFilter}
              onChange={(e) => {
                setReasonCategoryFilter((e.target.value as QualifiedReasonCategory) || '');
                setReasonCodeFilter('');
              }}
              className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">Any reason group</option>
              {QUALIFIED_REASON_CATEGORIES.map((category) => (
                <option key={category.code} value={category.code}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          {reasonCategoryFilter === 'infertility_issue' && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Infertility reason
              </label>
              <select
                value={reasonCodeFilter}
                onChange={(e) => setReasonCodeFilter(e.target.value)}
                className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">Any infertility reason</option>
                {reasonOptions.map((reason) => (
                  <option key={reason.code} value={reason.code}>
                    {reason.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {reasonCategoryFilter === 'gynic_issues' && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-600">
              Gynic Issues does not need an extra subreason.
            </div>
          )}
        </div>
      );
    }

    if (activeFilterSection === 'creation_date') {
      return renderDateChoices(createdPreset, setCreatedPreset, createdFrom, createdTo, setCreatedFrom, setCreatedTo);
    }

    if (activeFilterSection === 'assignment_date') {
      return renderDateChoices(
        assignmentPreset,
        setAssignmentPreset,
        assignmentFrom,
        assignmentTo,
        setAssignmentFrom,
        setAssignmentTo
      );
    }

    return renderDateChoices(
      dispositionPreset,
      setDispositionPreset,
      dispositionFrom,
      dispositionTo,
      setDispositionFrom,
      setDispositionTo
    );
  }

  const handleManualLeadSuccess = async (_leadId?: string) => {
    leadsCache.delete(queryKey);
    await loadPage(queryKey, 1, true);
  };

  const handleExport = async () => {
    setExporting(true);
    setErr(null);
    try {
      await exportLeadsCsv({ ...qsBase, limit: 5000, offset: 0 });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-3 px-3 py-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search name or phone..."
            className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-3 pr-20 text-sm"
          />
          <button
            onClick={() => setQ(searchText.trim())}
            className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
            title="Search"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
          </button>
        </div>

        <button
          onClick={() => setShowFilters(true)}
          className={`relative flex h-9 w-9 items-center justify-center rounded-lg border ${
            activeFilterCount ? 'border-purple-300 bg-purple-50 text-purple-700' : 'border-slate-200 bg-white text-slate-500'
          }`}
          title="Advanced filters"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 6h16" />
            <path d="M7 12h10" />
            <path d="M10 18h4" />
          </svg>
          {activeFilterCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-purple-600 px-1 text-[10px] font-bold text-white">
              {activeFilterCount}
            </span>
          )}
        </button>

        {isPrivileged && (
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            title="Export filtered leads"
          >
            {exporting ? 'Exporting...' : 'Export'}
          </button>
        )}

        {canCreateManualLead && (
          <button
            onClick={() => setShowManualLeadModal(true)}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700"
            title="Add custom lead"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
      </div>

      {activeChips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {activeChips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={chip.onClear}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700"
            >
              <span>{chip.label}</span>
              <span className="text-slate-400">x</span>
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          {loading || refreshing ? 'Updating results...' : `${leads.length} loaded`}
        </div>
      </div>

      {loading && leads.length === 0 && (
        <div className="space-y-3 py-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="h-28 animate-pulse rounded-2xl bg-slate-100" />
          ))}
        </div>
      )}
      {err && (
        <div className="rounded-lg bg-red-50 p-3 text-center text-sm text-red-700">
          <div>{err}</div>
          <button
            onClick={() => {
              const cached = leadsCache.get(queryKey);
              void loadPage(queryKey, (cached?.page ?? 0) + 1, !cached?.items.length);
            }}
            className="mt-2 rounded-md bg-red-100 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-200"
          >
            Retry
          </button>
        </div>
      )}
      {!loading && !err && leads.length === 0 && (
        <p className="py-16 text-center text-sm text-slate-500">No records found</p>
      )}

      <div className="space-y-3">
        {leads.map((l) => (
          <LeadCard
            key={l.id}
            lead={l}
            onSelect={onSelect}
            me={me}
            showStatus
            selected={selectedId === l.id}
          />
        ))}
      </div>

      <div ref={sentinelRef} className="h-8" />

      {loadingMore && (
        <div className="py-3 text-center text-xs font-medium text-slate-500">Loading more records...</div>
      )}

      {!hasMore && leads.length > 0 && (
        <div className="py-3 text-center text-xs text-slate-400">No more records</div>
      )}

      {showFilters && (
        <div className="fixed inset-0 z-40 flex items-end bg-black/35 p-0 sm:items-center sm:justify-center sm:p-4">
          <div className="flex max-h-[88vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:max-w-4xl sm:rounded-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <div className="text-lg font-semibold text-purple-700">Advanced Search</div>
                <div className="text-xs text-slate-500">{activeFilterCount} filters selected</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={clearFilters}
                  disabled={activeFilterCount === 0}
                  className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 disabled:opacity-50"
                >
                  Reset
                </button>
                <button
                  onClick={() => setShowFilters(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
                  title="Close filters"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="grid min-h-0 flex-1 overflow-hidden sm:grid-cols-[220px_1fr]">
              <div className="overflow-y-auto border-r border-slate-200 bg-slate-50 p-3 text-sm font-medium text-slate-600">
                {FILTER_SECTIONS.map((section) => {
                  const activeSection = section.id === activeFilterSection;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveFilterSection(section.id)}
                      className={`w-full border-l-4 px-3 py-3 text-left ${
                        activeSection
                          ? 'border-purple-600 bg-purple-50 text-purple-700'
                          : 'border-transparent hover:bg-slate-100'
                      }`}
                    >
                      {section.label}
                    </button>
                  );
                })}
              </div>

              <div className="overflow-y-auto px-6 py-6">
                {renderRightPanel()}
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <label className="flex items-center gap-3 text-sm font-medium text-slate-800">
                <input
                  type="checkbox"
                  checked={includeClosed}
                  onChange={(e) => setIncludeClosed(e.target.checked)}
                  className="h-4 w-4 accent-purple-700"
                />
                Include converted and lost leads
              </label>

              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => setShowFilters(false)}
                  className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowFilters(false)}
                  className="rounded-full bg-purple-700 px-4 py-2 text-sm font-semibold text-white"
                >
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {canCreateManualLead && (
        <ManualLeadModal
          isOpen={showManualLeadModal}
          onClose={() => setShowManualLeadModal(false)}
          onSuccess={handleManualLeadSuccess}
        />
      )}
    </div>
  );
}
